/** @jsx jsx */

import {
  Fragment as _,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
} from 'react';
import { css, jsx } from '@emotion/core';
import { values, colors } from 'styles/variables';
import { useSelector, useDispatch } from 'react-redux';
import getBlockById from 'services/apiHelpers/getBlockById';
import { createContext } from 'react';
import { Flexbox } from '@chameleoncreator/creator-ui';
import getStore from 'services/getStore';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import Wrapper from './Wrapper';
import HiddenBackButton from './HiddenBackButton';
import BackgroundImage from './Background';

import next from './next';
import updateReadOnlyText from 'services/updateReadOnlyText';
import pubSub from 'services/pubSub';
import { createSelector } from 'reselect';
import headingsStyles from './headingsStyles';
import { MenuContainer } from './MenuContainer';
import { State } from 'store/store';
import { controlAudioAction } from 'actions/controlAudioAction';
import { useHeader } from 'services/useHeader';
import getTopics from 'services/apiHelpers/getTopics';
import { getIsFirstInteraction } from 'services/getIsFirstInteraction';
import { ModuleContext } from 'components/Module/Module_v2';
import { InfoBubbleContainer } from 'components/Editor/EditorUtilities/UI/Bubble';
import { moduleNavigationActions } from 'components/NavigationSettings/ModuleNavigation';
import { updateInteractionsOpenAction } from 'actions/updateInteractionsOpenAction';
import { buttonLayouts } from './buttonLayouts';
import { updateGlobalFontStylesFromTheme } from 'styles/updateGlobalFontStylesFromTheme';
import './highlightActiveElement';
import { Orbs, getAnimatedOrbsDefaultValues } from 'components/Module/Orbs';

const store = getStore();
const renderCount = {};

const AUDIO_FILE_DEFAULT =
  'https://freesound.org/data/previews/542/542450_5674468-lq.mp3';

export const InteractionContext = createContext<any>(undefined);

const getInteractionsOpen = createSelector(
  (state: any) => state.interactionsOpen,
  interactionsOpen => interactionsOpen
);

let navigateToPreviewLocation = '';

// just totes for convenience right now
// will refactor
const getContinueButton = (
  section: HTMLTableSectionElement
): HTMLAnchorElement => {
  const buttons = Array.from(section?.querySelectorAll('a'));
  const continueBtn = buttons[buttons.length - 1] as HTMLAnchorElement;

  return continueBtn;
};

const getHiddenBackButton = (
  section: HTMLTableSectionElement,
  id: string
): HTMLButtonElement => {
  const hiddenBackButton = section?.querySelector(
    `#hidden-back-button-${id}`
  ) as HTMLButtonElement;

  return hiddenBackButton;
};
//

const ContainerLogical = (props: any) => {
  const moduleContext = useContext(ModuleContext);
  const { exit, setComplete, setCompleteAndExit, resetModule } = moduleContext;

  let {
    id,
    name,
    style,
    children,
    lockDown,
    includeAudio = false,
    audioSrc,
    autoPlay = false,
    stopAudioWhenModalIsOpened = true,
    controls = true,
    delay = 1,
    autoOpenNextSection = false,

    //
    backgroundStyle,

    // primaryOrbColor = '#FD7EB8',
    // secondaryOrbColor = '#B14FEC',
    // tertiaryOrbColor = '#00d9d3',
    // orbOpacity = (props?.orbOpacity || 100) * 0.01,
    // orbBlurStrength = 30,
    // orbSpeed = (props?.orbSpeed || 2) * 0.001,
  } = props;

  const {
    primaryOrbColor,
    secondaryOrbColor,
    tertiaryOrbColor,
    orbOpacity,
    orbBlurStrength,
    orbSpeed,
  } = getAnimatedOrbsDefaultValues(props);

  const { focusedNodeId, currentInteraction, theme } = useSelector(
    (state: State) => {
      return state;
    }
  );

  const dispatch = useDispatch();

  // check if nextInteractionMap is in 'order'
  // for the moment we'll use this dirty check
  const isFirstInteraction = getIsFirstInteraction(getTopics(), id);

  let {
    sId,
    background = undefined,
    backgroundImage = undefined,
    backgroundImageOpacity = 100,
    backgroundBlur = 0,
    src = undefined,
    textColor = style.foreground,
  } = getBlockById(id).props as any;

  const {
    backgroundGradient,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
    fullWidth = false,
  } = props;

  let buttonPosition = {
    left: 'start',
    center: 'center',
    right: 'end',
  }[props?.buttonPosition || 'center'];

  const linearGradient = backgroundGradient
    ? `linear-gradient(${background}, ${backgroundSecondaryColor})`
    : 'none';

  let interactionsOpen = useSelector(getInteractionsOpen);
  let isOpen = props.isOpen || interactionsOpen.includes(id);

  // dirty gotta try and cover for legacy backgroundImage
  if (src) {
    backgroundImage = `url('${src}') center / cover no-repeat`;
  }

  const section: any = useRef<HTMLElement>(null);
  const selected = id === focusedNodeId;

  const header = useHeader();
  const { headerStyle } = header.props;

  const { backButton, topicMenu } = header[headerStyle].props;

  let headerHeight = header[headerStyle].props.headerHeight;

  if (window.dev() && !isFirstInteraction) {
    headerHeight = 70;
  }

  const currentTopic = getCurrentTopic();

  const parsedTextColor =
    textColor ||
    (currentTopic && getBlockById(currentTopic.id)?.props?.textColor) ||
    theme.textColor ||
    '';

  const { fontSize = 16, lineHeight = 1.5, headings } = theme || {};

  const { h1, h2, h3, h4, h5, h6 } = headings || {};

  const contentContainer = useRef<HTMLDivElement>(null);

  const getHiddenMenuButton = (
    section: HTMLTableSectionElement
  ): HTMLButtonElement => {
    const hiddenMenuButton = section?.querySelector(
      `#hidden-button-menu-${id}`
    ) as HTMLButtonElement;

    return hiddenMenuButton;
  };

  useLayoutEffect(() => {
    if (!section.current) {
      section.current = document.querySelector(`#i${id}`);
    }

    !window.dev() && updateReadOnlyText(section.current);
  }, [parsedTextColor]);

  useEffect(() => {
    if (!section?.current) {
      return;
    }

    const hiddenBackButton = getHiddenBackButton(section.current, id);

    if (!hiddenBackButton) {
      return;
    }

    const onKeyDown = (e: any) => {
      const { key, shiftKey } = e;

      if (key === 'Tab' && shiftKey) {
        const continueBtn = getContinueButton(section.current);

        if (continueBtn) {
          // console.log('give focus to continue button !!!');
          continueBtn.focus({ preventScroll: true });
          e.preventDefault();
        }
      }
    };

    hiddenBackButton.addEventListener('keydown', onKeyDown);

    return () => {
      hiddenBackButton.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!section?.current) {
      return;
    }

    // // this needs to take into account that the continue ' button '
    // // now is an actual a tag !!!

    const continueBtn = getContinueButton(section.current);

    if (!continueBtn) {
      return;
    }

    const onKeyDown = (e: any) => {
      const { key, shiftKey } = e;

      if (shiftKey) {
        return;
      }

      if (key === 'Tab') {
        const hiddenBackButton = getHiddenBackButton(section.current, id);
        const hiddenMenuButton = getHiddenMenuButton(section.current);

        if (hiddenBackButton && !hiddenBackButton.disabled) {
          //
          hiddenBackButton.focus({ preventScroll: true });
        } else {
          hiddenMenuButton ? hiddenMenuButton.focus() : section.current.focus();
        }
        e.preventDefault();
      }
    };

    continueBtn.addEventListener('keydown', onKeyDown);

    return () => {
      continueBtn.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!lockDown) {
      return;
    }
  });

  useEffect(() => {
    if (!section.current) {
      return;
    }

    let tick = setInterval(() => {
      if (!section.current) {
        return;
      }
      //
      var scrollBarWidth = window.innerWidth - document.body.clientWidth;

      if (scrollBarWidth > 0) {
        clearInterval(tick);
        // if scrollBarWidth calcs to greater than 35
        // then wtf. wrong things are happening. don't update the padding
        // but if we're within that range. . .
        if (scrollBarWidth <= 35) {
          section.current.style.paddingLeft = `${scrollBarWidth}px`;
        }
      }
    }, 1000 / 12);

    return () => {
      clearInterval(tick);
    };
  }, []);

  useEffect(() => {
    if (!contentContainer.current) {
      return;
    }
    //
    // this is gonna get put someplace else
    // make it part of the hashChange maybe
    // but needs reference to the interaction sId/id
    //
    const anchorNavNextExit = e => {
      const { target } = e;
      const tagName = target.tagName.toLowerCase();
      const href = target.getAttribute('href');
      const {
        NEXT,
        EXIT,
        SET_MODULE_COMPLETE,
        SET_MODULE_COMPLETE_AND_EXIT,
        RESET_MODULE,
      } = moduleNavigationActions;

      const aNext = next(sId, id);

      if (tagName === 'a') {
        if ([NEXT, EXIT].includes(href)) {
          e.preventDefault();

          aNext(href);
          return;
        }

        if (
          [
            SET_MODULE_COMPLETE,
            SET_MODULE_COMPLETE_AND_EXIT,
            RESET_MODULE,
          ].includes(href)
        ) {
          e.preventDefault();

          href === SET_MODULE_COMPLETE && setComplete();
          href === SET_MODULE_COMPLETE_AND_EXIT && setCompleteAndExit();
          href === RESET_MODULE && resetModule();
          return;
        }
      }
    };

    contentContainer.current.addEventListener('click', anchorNavNextExit);

    return () => {
      contentContainer.current &&
        contentContainer.current.removeEventListener(
          'click',
          anchorNavNextExit
        );
    };
  }, []);

  useEffect(() => {
    if (window.dev()) {
      const data = includeAudio
        ? {
            id,
            audioSrc,
            autoPlay: window.dev() ? false : autoPlay,
            controls,
            delay,
          }
        : null;

      dispatch(controlAudioAction(data));
    }
  }, [includeAudio, audioSrc]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const sectionInViewSubscriber = pubSub.subscribe(
      'SECTION_IN_VIEW',
      ({ sectionInView }) => {
        if (sectionInView.id === id) {
          const data = includeAudio
            ? {
                id,
                audioSrc,
                autoPlay: window.dev() ? false : autoPlay,
                controls,
                delay,
                pause: false,
              }
            : null;

          dispatch(controlAudioAction(data));
        }
      }
    );

    return () => {
      sectionInViewSubscriber();
    };
  }, []);

  useEffect(() => {
    if (autoOpenNextSection) {
      store.dispatch(updateInteractionsOpenAction(id));
    }
  }, []);

  let isLocked = lockDown;

  // since the background gets set on the style on creation
  // kill it if we have a ' local ' background color
  // goal would be to remove it from the factory. but it's legacy from wizard
  // WINDING on down. . .
  const overideStyleBackground = props.hasOwnProperty('background')
    ? {
        background: background || '',
      }
    : {};

  if (!renderCount[id]) {
    renderCount[id] = 0;
  }

  // need to further work on all the concepts going on with the headerHeight
  // firstInteraction checking
  // responsive aspect etc
  // right now it's being spread over a couple places which feels super gross
  // need to bring it all under some unified solution

  let paddingTop = headerHeight;

  if (!isFirstInteraction && headerStyle === 'graphic') {
    paddingTop = 70;
  }

  if (screen.availWidth <= 480) {
    paddingTop = 50;
  }

  return (
    <InteractionContext.Provider
      value={{
        ...props.data,
        id,
        textColor: parsedTextColor,
        stopAudioWhenModalIsOpened,
        next: next(sId, id),
        isLocked,
        children,
      }}
    >
      {/* @ts-ignore */}
      <Flexbox
        as="section"
        id={`i${id}`}
        css={css`
          ${!isOpen &&
            `
            display: none;
          `}

          ${updateGlobalFontStylesFromTheme({
            fontSize,
            lineHeight,
            parsedTextColor,
            headingsStyles() {
              return headingsStyles({ h1, h2, h3, h4, h5, h6 });
            },
          })}

          [id*='content-container'] > div:last-child {
            ${window.dev()
              ? `margin-${
                  {
                    start: 'left',
                    end: 'right',
                  }[buttonPosition]
                }: -9px;`
              : ''}

            > div {
              ${buttonLayouts(buttonPosition)}
            }
          }
        `}
        style={{
          position: 'relative',
          overflowX: 'hidden',
          ...{
            ...style,
            ...overideStyleBackground,
            ...(backgroundGradient && {
              background: `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${background})`,
            }),
          },
        }}
        py={4}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={'100vh'}
        width={['100%']}
        paddingTop={paddingTop}
        data-section
        data-section-text-color={parsedTextColor}
        aria-label={`${props.name ? `${props.name}—` : ''}${
          props.sectionLabel
        }`}
        tabIndex={-1}
      >
        {/* {(backgroundStyle === 'backgroundImage' && backgroundImage) || (backgroundStyle === undefined && backgroundImage) && ( */}
        {[undefined, 'backgroundImage'].includes(backgroundStyle) &&
          backgroundImage && (
            <BackgroundImage
              key={backgroundImage}
              backgroundImage={backgroundImage}
              backgroundImageOpacity={backgroundImageOpacity}
              backgroundBlur={backgroundBlur}
            />
          )}

        {backgroundStyle === 'animatedOrbs' && (
          <Orbs
            {...{
              orbsFor: 'interaction',
              primaryOrbColor,
              secondaryOrbColor,
              tertiaryOrbColor,
              orbOpacity,
              orbBlurStrength,
              orbSpeed,
            }}
          />
        )}

        {backButton && <HiddenBackButton id={id} />}
        {topicMenu && <MenuContainer id={id} rootSelector="#menu-portal" />}

        {/* @ts-ignore */}
        <Flexbox
          ref={contentContainer}
          id={`i${id}-content-container`}
          position="relative"
          flexDirection="column"
          justifyContent="center"
          width={['100%']}
          maxWidth={['100%', null, fullWidth ? '100%' : '1100px']}
          // tabIndex={-1}
          css={css({
            ...(includeAudio &&
              audioSrc &&
              controls && {
                '@media (max-width: 768px)': {
                  paddingBottom: 20,
                },
              }),
          })}
        >
          {children}
        </Flexbox>

        {window.dev() && (
          <Wrapper
            id={id}
            focusedNodeId={focusedNodeId}
            headerHeight={headerHeight}
          />
        )}
      </Flexbox>

      <InfoBubbleContainer />
    </InteractionContext.Provider>
  );
};

export default ContainerLogical;
